// SignInCard.jsx

import * as React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { loginSuccess } from '../slices/authSlice';
import { storeAuthToken } from '../utils/authToken';
import ForgotPassword from './ForgotPassword';
import { GoogleIcon, FacebookIcon, SitemarkIcon } from './CustomIcons';
import { io } from 'socket.io-client';

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    [theme.breakpoints.up('sm')]: {
        width: '450px',
    },
}));

export default function SignInCard() {
    const [loginInput, setLoginInput] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);
    const [twoFactorRequired, setTwoFactorRequired] = useState(false);
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [twoFactorUserId, setTwoFactorUserId] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const redirectTo = React.useMemo(() => {
        const fromLocation = location.state?.from;
        if (!fromLocation) {
            return '/dashboard';
        }

        const pathname = fromLocation.pathname || '/dashboard';
        const search = fromLocation.search || '';
        const hash = fromLocation.hash || '';

        return `${pathname}${search}${hash}`;
    }, [location]);

    const handleClickOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const res = await axios.post('/api/users/verify-2fa', {
                userId: twoFactorUserId,
                code: twoFactorCode,
                rememberMe,
            });
            const { token } = res.data;
            if (!token) {
                setError('Verification failed: no token returned.');
                return;
            }
            storeAuthToken(token, rememberMe);

            const meRes = await axios.get('/api/users/me', {
                headers: { Authorization: `Bearer ${token}` },
            });
            const user = meRes.data;

            dispatch(loginSuccess({ token, user }));

            const socket = io('/', {
                transports: ['websocket', 'polling'],
            });
            socket.emit('join', user._id);
            window.__SOCKET__ = socket;

            navigate(redirectTo, { replace: true });
        } catch (err) {
            console.error('2FA verification failed:', err);
            if (err.response?.data?.msg) {
                setError(err.response.data.msg);
            } else {
                setError('An error occurred. Please try again.');
            }
        }
    };

    const validateInputs = () => {
        let isValid = true;
        if (!loginInput) {
            setError('Please enter a username or email.');
            isValid = false;
        }
        if (!password || password.length < 6) {
            setError('Password must be at least 6 characters long.');
            isValid = false;
        }
        return isValid;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        if (!validateInputs()) return;

        try {
            // 1) POST to /api/users/login
            const response = await axios.post('/api/users/login', {
                loginInput,
                password,
                rememberMe,
            });

            if (response.data.twoFactorRequired) {
                setTwoFactorRequired(true);
                setTwoFactorUserId(response.data.userId);
                return;
            }

            const { token } = response.data;
            if (!token) {
                setError('Login failed: no token returned.');
                return;
            }
            storeAuthToken(token, rememberMe);

            // 2) Fetch /api/users/me to get full user object
            const meRes = await axios.get('/api/users/me', {
                headers: { Authorization: `Bearer ${token}` },
            });
            const user = meRes.data;

            // 3) Dispatch loginSuccess with token & user
            dispatch(loginSuccess({ token, user }));

            // 4) Initialize socket.io and emit 'join'
            const socket = io('/', {
                transports: ['websocket', 'polling'],
            });
            socket.emit('join', user._id);

            // Optionally store socket globally for reuse
            window.__SOCKET__ = socket;

            // 5) Navigate to dashboard
            navigate(redirectTo, { replace: true });
        } catch (err) {
            console.error('Login failed:', err);
            if (err.response?.data?.msg) {
                setError(err.response.data.msg);
            } else {
                setError('An error occurred. Please try again.');
            }
        }
    };

    return (
        <Card variant="outlined">
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <SitemarkIcon />
            </Box>
            <Typography
                component="h1"
                variant="h4"
                sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
            >
                Sign In
            </Typography>
            {error && (
                <Typography color="error" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}
            {!twoFactorRequired ? (
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
                >
                    <FormControl>
                        <FormLabel htmlFor="loginInput">Username or Email</FormLabel>
                        <TextField
                            value={loginInput}
                            onChange={(e) => setLoginInput(e.target.value)}
                            id="loginInput"
                            type="text"
                            name="loginInput"
                            placeholder="Enter your username or email"
                            autoComplete="username"
                            autoFocus
                            required
                            fullWidth
                            variant="outlined"
                        />
                    </FormControl>
                    <FormControl>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <FormLabel htmlFor="password">Password</FormLabel>
                            <Link
                                component="button"
                                type="button"
                                onClick={handleClickOpen}
                                variant="body2"
                                sx={{ alignSelf: 'baseline' }}
                            >
                                Forgot your password?
                            </Link>
                        </Box>
                        <TextField
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            name="password"
                            placeholder="••••••"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            required
                            fullWidth
                            variant="outlined"
                        />
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Remember me"
                    />
                    <ForgotPassword open={open} handleClose={handleClose} />
                    <Button type="submit" fullWidth variant="contained">
                        Sign In
                    </Button>
                    <Typography sx={{ textAlign: 'center' }}>
                        Don't have an account?{' '}
                        <span>
                            <Link href="/register" variant="body2" sx={{ alignSelf: 'center' }}>
                                Sign up
                            </Link>
                        </span>
                    </Typography>
                </Box>
            ) : (
                <Box
                    component="form"
                    onSubmit={handleVerify}
                    noValidate
                    sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
                >
                    <FormControl>
                        <FormLabel htmlFor="code">Verification Code</FormLabel>
                        <TextField
                            value={twoFactorCode}
                            onChange={(e) => setTwoFactorCode(e.target.value)}
                            id="code"
                            type="text"
                            name="code"
                            placeholder="Enter the code sent to your email"
                            autoFocus
                            required
                            fullWidth
                            variant="outlined"
                        />
                    </FormControl>
                    <Button type="submit" fullWidth variant="contained">
                        Verify Code
                    </Button>
                </Box>
            )}
            <Divider>or</Divider>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => alert('Sign in with Google')}
                    startIcon={<GoogleIcon />}
                >
                    Sign in with Google
                </Button>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => alert('Sign in with Facebook')}
                    startIcon={<FacebookIcon />}
                >
                    Sign in with Facebook
                </Button>
            </Box>
        </Card>
    );
}
